/**
 * Frontend Asset Metadata Types
 * Matching the backend structure for marketing assets
 */

// Base metadata interface for all assets
export interface BaseAssetMetadata {
  asset_id: string;
  type: AssetType;
  created_date: string;
  modified_date: string;
  owner: string;
  status: AssetStatus;
  reviewer?: string;
  campaign?: string;
  version: number;
  parent_item?: string;
  related_assets?: string[];
  tags: string[];
  target_audience?: string;
  review: {
    comments: string[];
    approved: boolean;
    approved_by?: string;
    approval_date?: string;
  };
}

// Asset type enum
export type AssetType = 'email' | 'image' | 'video' | 'seo_content' | 'faq' | 'site_content' | 'ad' | 'podcast';

// Asset status enum
export type AssetStatus = 'draft' | 'in_review' | 'approved' | 'published' | 'archived';

// Email Asset Metadata
export interface EmailMetadata extends BaseAssetMetadata {
  type: 'email';
  subject: string;
  preheader: string;
  from_name: string;
  from_email: string;
  reply_to?: string;
  send_date?: string;
  segment?: string;
  cta_url?: string;
  cta_text?: string;
  screenshots?: string[];
  mobile_responsive: boolean;
  template_id?: string;
  personalization_tokens?: string[];
  esp?: 'mailchimp' | 'sendgrid' | 'hubspot' | 'klaviyo' | 'other';
  test_emails?: string[];
}

// Image Asset Metadata
export interface ImageMetadata extends BaseAssetMetadata {
  type: 'image';
  dimensions: {
    width: number;
    height: number;
  };
  file_format: 'jpg' | 'png' | 'gif' | 'svg' | 'webp';
  alt_text: string;
  caption?: string;
  focal_point?: {
    x: number;
    y: number;
  };
  dominant_colors?: string[];
  usage_rights?: 'royalty_free' | 'licensed' | 'owned' | 'creative_commons';
  photographer?: string;
  source?: string;
  expiry_date?: string;
  compression_quality?: number;
  variants?: {
    size: string;
    url: string;
    dimensions: { width: number; height: number };
  }[];
}

// Video Asset Metadata
export interface VideoMetadata extends BaseAssetMetadata {
  type: 'video';
  duration_seconds: number;
  dimensions: {
    width: number;
    height: number;
  };
  frame_rate: number;
  bitrate: number;
  codec: string;
  format: 'mp4' | 'mov' | 'avi' | 'webm' | 'mkv';
  thumbnail_url?: string;
  captions_available: boolean;
  captions_languages?: string[];
  transcript?: string;
  chapters?: {
    title: string;
    start_time: number;
    end_time: number;
  }[];
  production_company?: string;
  director?: string;
  talent?: string[];
  location?: string;
  shooting_date?: string;
}

// SEO Content Metadata
export interface SEOContentMetadata extends BaseAssetMetadata {
  type: 'seo_content';
  page_title: string;
  meta_description: string;
  url_slug: string;
  canonical_url?: string;
  target_keyword: string;
  secondary_keywords?: string[];
  word_count: number;
  reading_time_minutes: number;
  h1_tag: string;
  h2_tags?: string[];
  internal_links?: {
    text: string;
    url: string;
  }[];
  external_links?: {
    text: string;
    url: string;
    no_follow: boolean;
  }[];
  og_title?: string;
  og_description?: string;
  og_image?: string;
  schema_markup?: any;
  content_score?: number;
  readability_score?: number;
}

// FAQ Asset Metadata
export interface FAQMetadata extends BaseAssetMetadata {
  type: 'faq';
  category: string;
  questions_count: number;
  faqs: {
    id: string;
    question: string;
    answer: string;
    order: number;
    helpful_yes?: number;
    helpful_no?: number;
    related_articles?: string[];
    tags?: string[];
  }[];
  last_reviewed?: string;
  expert_reviewer?: string;
  schema_markup?: any;
  display_format?: 'accordion' | 'list' | 'cards';
  search_indexed: boolean;
}

// Site Content Metadata
export interface SiteContentMetadata extends BaseAssetMetadata {
  type: 'site_content';
  page_type: 'homepage' | 'landing' | 'product' | 'service' | 'about' | 'contact' | 'blog' | 'other';
  url_path: string;
  sections: {
    id: string;
    type: 'hero' | 'features' | 'testimonials' | 'cta' | 'content' | 'gallery' | 'form';
    title?: string;
    content?: string;
    order: number;
  }[];
  navigation_title?: string;
  breadcrumbs?: string[];
  template?: string;
  layout?: 'full_width' | 'sidebar_left' | 'sidebar_right' | 'centered';
  custom_css?: string;
  custom_js?: string;
  a_b_test?: {
    variant: 'control' | 'variant_a' | 'variant_b';
    test_id: string;
  };
}

// Ad Asset Metadata
export interface AdMetadata extends BaseAssetMetadata {
  type: 'ad';
  platform: 'google_ads' | 'facebook' | 'instagram' | 'linkedin' | 'twitter' | 'tiktok' | 'display';
  ad_format: string;
  campaign_name: string;
  ad_group?: string;
  headline: string;
  description?: string;
  cta_text: string;
  cta_url: string;
  display_url?: string;
  creative_assets?: {
    type: 'image' | 'video' | 'carousel';
    url: string;
    alt_text?: string;
  }[];
  targeting?: {
    demographics?: any;
    interests?: string[];
    behaviors?: string[];
    custom_audiences?: string[];
    locations?: string[];
  };
  budget?: {
    daily?: number;
    lifetime?: number;
    currency: string;
  };
  schedule?: {
    start_date: string;
    end_date?: string;
    day_parting?: any;
  };
  performance_data?: {
    impressions?: number;
    clicks?: number;
    ctr?: number;
    conversions?: number;
    spend?: number;
  };
}

// Podcast Asset Metadata
export interface PodcastMetadata extends BaseAssetMetadata {
  type: 'podcast';
  episode_title: string;
  episode_number?: number;
  season_number?: number;
  duration_seconds: number;
  description: string;
  show_notes?: string;
  transcript?: string;
  hosts?: string[];
  guests?: {
    name: string;
    bio?: string;
    links?: string[];
  }[];
  chapters?: {
    title: string;
    start_time: number;
    description?: string;
  }[];
  audio_format: 'mp3' | 'wav' | 'm4a' | 'ogg';
  bitrate: number;
  sample_rate: number;
  mono_stereo: 'mono' | 'stereo';
  explicit_content: boolean;
  categories?: string[];
  keywords?: string[];
  publish_date?: string;
  platforms?: {
    name: string;
    url: string;
  }[];
  sponsors?: {
    name: string;
    message?: string;
    timestamp?: number;
  }[];
}

// Union type for all asset metadata
export type AssetMetadata =
  | EmailMetadata
  | ImageMetadata
  | VideoMetadata
  | SEOContentMetadata
  | FAQMetadata
  | SiteContentMetadata
  | AdMetadata
  | PodcastMetadata;

// Asset entity that combines file info with metadata
export interface MarketingAsset {
  id: string;
  type: AssetType;
  title: string;
  description?: string;
  status: AssetStatus;
  metadata: AssetMetadata;
  file_path: string;
  file_type: string;
  file_size_bytes: number;
  campaign_id?: string;
  owner_id: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  presigned_url?: string;
}

// Type guards
export function isEmailMetadata(metadata: AssetMetadata): metadata is EmailMetadata {
  return metadata.type === 'email';
}

export function isImageMetadata(metadata: AssetMetadata): metadata is ImageMetadata {
  return metadata.type === 'image';
}

export function isVideoMetadata(metadata: AssetMetadata): metadata is VideoMetadata {
  return metadata.type === 'video';
}

export function isSEOContentMetadata(metadata: AssetMetadata): metadata is SEOContentMetadata {
  return metadata.type === 'seo_content';
}

export function isFAQMetadata(metadata: AssetMetadata): metadata is FAQMetadata {
  return metadata.type === 'faq';
}

export function isSiteContentMetadata(metadata: AssetMetadata): metadata is SiteContentMetadata {
  return metadata.type === 'site_content';
}

export function isAdMetadata(metadata: AssetMetadata): metadata is AdMetadata {
  return metadata.type === 'ad';
}

export function isPodcastMetadata(metadata: AssetMetadata): metadata is PodcastMetadata {
  return metadata.type === 'podcast';
}

// Helper to get asset type icon
export function getAssetTypeIcon(type: AssetType): string {
  const icons: Record<AssetType, string> = {
    email: '📧',
    image: '🖼️',
    video: '🎬',
    seo_content: '🔍',
    faq: '❓',
    site_content: '🌐',
    ad: '📢',
    podcast: '🎙️',
  };
  return icons[type] || '📄';
}

// Helper to get asset type label
export function getAssetTypeLabel(type: AssetType): string {
  const labels: Record<AssetType, string> = {
    email: 'Email',
    image: 'Image',
    video: 'Video',
    seo_content: 'SEO Content',
    faq: 'FAQ',
    site_content: 'Site Content',
    ad: 'Advertisement',
    podcast: 'Podcast',
  };
  return labels[type] || 'Asset';
}